/**
 * OAuth provider buttons — sorted alphabetically to match v1 production.
 *
 * v1 layout: 2×2 Grid, maxWidth 350px, sorted A–Z (Apple, GitHub, Google, Microsoft).
 * Each button: rounded-[10px], 1px border, fontWeight 300, icon 30×30 with ml-5 mr-3.
 *
 * Opens the server OAuth endpoint which handles the full flow:
 * server callback → Dexie Cloud OTP bridge → redirect with ?email=&otpId=&otp=
 *
 * On native: uses @capacitor/browser (in-app browser) so the
 * deep link redirect (totodo:///) is properly caught by AppUrlListener.
 * On web: standard window.location.href redirect.
 */

import { useCallback, useMemo, type ReactElement } from "react"
import { Capacitor } from "@capacitor/core"
import { isDesktopApp } from "@/shared/utils/platform/desktopDetection"

interface OAuthProviderButtonsProps {
  googleUri: string
  githubUri: string
  appleUri: string
  microsoftUri: string
}

interface LoginProvider {
  name: string
  icon: ReactElement
  uri: string
}

function getOriginParameter(): string {
  if (typeof window === "undefined") return "localhost"

  // On native, send "localhost" so the server's shouldOpenNativeApp()
  // returns true and redirects via the custom URL scheme (totodo:///).
  // NOTE: "totodo.app" CANNOT be used here — shouldOpenNativeApp treats
  // it as the web production domain and returns false!
  if (Capacitor.isNativePlatform()) return "localhost"

  // Desktop (Tauri): send the app's own origin. It registers no totodo:// URL
  // scheme, so the server must treat it as web and redirect the sign-in back
  // here instead of to the website — otherwise the app sits on
  // "Opening To To-Do..." with a fallback link that does nothing.
  if (isDesktopApp()) return window.location.origin

  const host = window.location.hostname
  if (host.includes("totodo.app")) return "totodo.app"
  if (host.includes("totodo.io")) return "totodo.io"

  // Include port for localhost so shouldOpenNativeApp() can distinguish
  // web dev ("localhost:5150") from native ("totodo.app")
  const port = window.location.port
  if (host === "localhost" && port) return `localhost:${port}`
  return host
}

async function openAuthUrl(url: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    // Use in-app browser on native — the OAuth callback will redirect
    // to our custom URL scheme (totodo:///) which is caught by AppUrlListener
    try {
      const { Browser } = await import("@capacitor/browser")
      await Browser.open({ url, presentationStyle: "popover" })
      return
    } catch {
      // Fallback to standard redirect if Browser plugin fails
      console.warn("[OAuth] Browser.open() failed, falling back to redirect")
    }
  }
  window.location.href = url
}

export function OAuthProviderButtons({
  googleUri,
  githubUri,
  appleUri,
  microsoftUri,
}: OAuthProviderButtonsProps) {
  const handleOAuth = useCallback(async (baseUri: string) => {
    const origin = getOriginParameter()
    await openAuthUrl(`${baseUri}?origin=${origin}`)
  }, [])

  // v1: providers are sorted alphabetically → Apple, GitHub, Google, Microsoft
  const providers: LoginProvider[] = useMemo(
    () =>
      [
        { name: "Apple", icon: <AppleIcon />, uri: appleUri },
        { name: "GitHub", icon: <GitHubIcon />, uri: githubUri },
        { name: "Google", icon: <GoogleIcon />, uri: googleUri },
        { name: "Microsoft", icon: <MicrosoftIcon />, uri: microsoftUri },
      ].sort((a, b) => a.name.localeCompare(b.name)),
    [appleUri, githubUri, googleUri, microsoftUri],
  )

  return (
    <div
      className="grid grid-cols-2 mx-auto"
      style={{ maxWidth: 350, gap: 10 }}
    >
      {providers.map((p) => (
        <button
          key={p.name}
          type="button"
          onClick={() => handleOAuth(p.uri)}
          className="flex items-center w-full rounded-[10px] border border-foreground/20 bg-transparent p-2 text-foreground font-light cursor-pointer transition-colors hover:bg-foreground/5"
          style={{ textTransform: "none" }}
        >
          <span className="flex items-center justify-center ml-5 mr-3">
            {p.icon}
          </span>
          {p.name}
        </button>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Provider SVG Icons (ported from v1 — 30×30 to match v1 production size)
// ---------------------------------------------------------------------------

function GoogleIcon() {
  return (
    <svg
      width="30"
      height="30"
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill="#FFC107"
        d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
      />
      <path
        fill="#FF3D00"
        d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
      />
      <path
        fill="#1976D2"
        d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
      />
    </svg>
  )
}

function GitHubIcon() {
  return (
    <svg
      width="30"
      height="30"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className="fill-foreground"
    >
      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
    </svg>
  )
}

function AppleIcon() {
  return (
    <svg
      width="30"
      height="30"
      viewBox="0 0 50 50"
      xmlns="http://www.w3.org/2000/svg"
      className="fill-foreground"
    >
      <path d="M 44.527344 34.75 C 43.449219 37.144531 42.929688 38.214844 41.542969 40.328125 C 39.601563 43.28125 36.863281 46.96875 33.480469 46.992188 C 30.46875 47.019531 29.691406 45.027344 25.601563 45.0625 C 21.515625 45.082031 20.664063 47.03125 17.648438 47 C 14.261719 46.96875 11.671875 43.648438 9.730469 40.699219 C 4.300781 32.429688 3.726563 22.734375 7.082031 17.578125 C 9.457031 13.921875 13.210938 11.773438 16.738281 11.773438 C 20.332031 11.773438 22.589844 13.746094 25.558594 13.746094 C 28.441406 13.746094 30.195313 11.769531 34.351563 11.769531 C 37.492188 11.769531 40.8125 13.480469 43.1875 16.433594 C 35.421875 20.691406 36.683594 31.78125 44.527344 34.75 Z M 31.195313 8.46875 C 32.707031 6.527344 33.855469 3.789063 33.4375 1 C 30.972656 1.167969 28.089844 2.742188 26.40625 4.78125 C 24.878906 6.640625 23.613281 9.398438 24.105469 12.066406 C 26.796875 12.152344 29.582031 10.546875 31.195313 8.46875 Z" />
    </svg>
  )
}

function MicrosoftIcon() {
  return (
    <svg
      width="30"
      height="30"
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path fill="#ff5722" d="M6 6H22V22H6z" />
      <path fill="#4caf50" d="M26 6H42V22H26z" />
      <path fill="#ffc107" d="M26 26H42V42H26z" />
      <path fill="#03a9f4" d="M6 26H22V42H6z" />
    </svg>
  )
}
